import React from 'react';
import { Form, Modal, Input, Button, Space, Switch, Divider } from 'antd';
import { useIntl } from 'react-intl';

const { TextArea } = Input;

interface EmailBody {
  html: string;
  text: string;
}

interface EmailFormData {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: EmailBody;
  replyTo?: string;
  from?: string;
}

interface EmailFormProps {
  openSendEmailModal: boolean;
  setOpenSendEmailModal: (open: boolean) => void;
  onSendEmail: (emailData: EmailFormData) => void;
  emailFrom: string | null;
}

const EmailForm = ({
  openSendEmailModal,
  setOpenSendEmailModal,
  onSendEmail,
  emailFrom
}: EmailFormProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [isHtmlMode, setIsHtmlMode] = React.useState(true);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const emailData: EmailFormData = {
        to: values.to || '',
        subject: values.subject || '',
        body: {
          html: isHtmlMode ? values.body : `<p>${values.body}</p>`,
          text: isHtmlMode ? values.body.replace(/<[^>]*>/g, '') : values.body
        }
      };

      if(values.cc ) {
        emailData.cc = values.cc;
      }

      if(values.bcc) {
        emailData.bcc = values.bcc;
      }

      if(values.replyTo) {
        emailData.replyTo = values.replyTo;
      }

      if(values.from) {
        emailData.from = values.from;
      }

      onSendEmail(emailData);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    setOpenSendEmailModal(false);
    form.resetFields();
  };

  return (
      <Modal
          title={intl.formatMessage({ id: 'email.send.title' })}
          visible={openSendEmailModal}
          onOk={handleOk}
          onCancel={handleCancel}
          width={600}
          footer={[
              <Button key="cancel" onClick={handleCancel}>
                  {intl.formatMessage({ id: 'common.cancel' })}
              </Button>,
              <Button key="send" type="primary" onClick={handleOk}>
                  {intl.formatMessage({ id: 'email.send.button' })}
              </Button>,
      ]}
    >
          <Form
              form={form}
              layout="vertical"
              initialValues={{
          from: 'noreply@simplifa.com',
          replyTo: 'support@simplifa.com',
          to: emailFrom
        }}
      >
              {/* Required Fields */}
              <Form.Item
                  label={intl.formatMessage({ id: 'email.to.label' })}
                  name="to"
                  rules={[
            { 
              required: true, 
              message: intl.formatMessage({ id: 'email.to.required' }) 
            },
            {
              type: 'email',
              message: intl.formatMessage({ id: 'email.invalid' })
            }
          ]}
        >
                  <Input placeholder="customer@example.com" />
              </Form.Item>

              <Form.Item
                  label={intl.formatMessage({ id: 'email.subject.label' })}
                  name="subject"
                  rules={[
            { 
              required: true, 
              message: intl.formatMessage({ id: 'email.subject.required' }) 
            }
          ]}
        >
                  <Input placeholder="Welcome to Simplifa" />
              </Form.Item>

              {/* Body Content */}
              <Form.Item
                  label={intl.formatMessage({ id: 'email.body.label' })}
                  name="body"
                  rules={[
            { 
              required: true, 
              message: intl.formatMessage({ id: 'email.body.required' }) 
            }
          ]}
        >
                  <TextArea
                      rows={6}
                      placeholder={
              isHtmlMode 
                ? "<h1>Welcome!</h1><p>We're excited to have you on board.</p>" 
                : "Welcome! We're excited to have you on board."
            }
          />
              </Form.Item>

              {/* HTML/Text Toggle */}
              {/* <Form.Item>
                  <Space>
                      <span>{intl.formatMessage({ id: 'email.format.label' })}</span>
                      <Switch
                          checkedChildren="HTML"
                          unCheckedChildren="Text"
                          checked={isHtmlMode}
                          onChange={setIsHtmlMode}
            />
                  </Space>
              </Form.Item> */}

              {/* Advanced Options Toggle */}
              <Button
                  type="link"
                  size="small"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  style={{ padding: 0, marginBottom: 16 }}
        >
                  {showAdvanced 
            ? intl.formatMessage({ id: 'email.advanced.hide' })
            : intl.formatMessage({ id: 'email.advanced.show' })
          }
              </Button>

              {showAdvanced && (
              <>
                  <Divider style={{ margin: '16px 0' }} />
            
                  {/* Optional Fields */}
                  <Form.Item
                      label={intl.formatMessage({ id: 'email.cc.label' })}
                      name="cc"
                      rules={[
                {
                  type: 'email',
                  message: intl.formatMessage({ id: 'email.invalid' })
                }
              ]}
            >
                      <Input placeholder="manager@company.com" />
                  </Form.Item>

                  <Form.Item
                      label={intl.formatMessage({ id: 'email.bcc.label' })}
                      name="bcc"
                      rules={[
                {
                  type: 'email',
                  message: intl.formatMessage({ id: 'email.invalid' })
                }
              ]}
            >
                      <Input placeholder="archive@company.com" />
                  </Form.Item>

                  <Form.Item
                      label={intl.formatMessage({ id: 'email.replyTo.label' })}
                      name="replyTo"
                      rules={[
                {
                  type: 'email',
                  message: intl.formatMessage({ id: 'email.invalid' })
                }
              ]}
            >
                      <Input placeholder="support@simplifa.com" />
                  </Form.Item>

                  <Form.Item
                      label={intl.formatMessage({ id: 'email.from.label' })}
                      name="from"
                      rules={[
                {
                  type: 'email',
                  message: intl.formatMessage({ id: 'email.invalid' })
                }
              ]}
            >
                      <Input placeholder="noreply@simplifa.com" />
                  </Form.Item>
              </>
        )}
          </Form>
      </Modal>
  );
};

export default EmailForm;